<div class="tennis-serve-library">
  <!-- Left column: upload + filters -->
  <div class="sidebar">
    <h2 class="sidebar__title">Tennis Serve Library</h2>
    <p class="sidebar__subtitle">
      Upload your serve videos, organize by labels, and track elbow angles on your trophy pose.
    </p>

    <!-- Upload form -->
    <form [formGroup]="mediaForm" (ngSubmit)="createItem()" class="upload-form">
      <div class="field">
        <label>Title</label>
        <input
          type="text"
          formControlName="title"
          placeholder="E.g. Match vs John – 2nd set deuce court"
        />
      </div>

      <div class="field">
        <label>Notes</label>
        <textarea
          formControlName="description"
          rows="3"
          placeholder="What were you working on in this serve session?"
        ></textarea>
      </div>

      <div class="field">
        <label>Labels (comma separated)</label>
        <input
          type="text"
          formControlName="labels"
          placeholder="match, practice, deuce, ad, slice, kick"
        />
      </div>

      <div class="field">
        <label>Card color</label>
        <div class="color-palette">
          <button
            type="button"
            *ngFor="let c of colorPalette"
            class="color-swatch"
            [style.backgroundColor]="c"
            [class.selected]="mediaForm.value.color === c"
            (click)="mediaForm.patchValue({ color: c })"
          ></button>
        </div>
      </div>

      <div class="field field--inline">
        <label class="checkbox">
          <input type="checkbox" formControlName="pinned" />
          <span>Pin this serve</span>
        </label>
      </div>

      <div class="field file-field">
        <label class="file-label">
          <input
            type="file"
            accept="video/*"
            (change)="onFileSelected($event)"
          />
          <span class="file-button">Choose video</span>
          <span class="file-name">{{ selectedFileName }}</span>
        </label>
      </div>

      <button
        class="submit-button"
        type="submit"
        [disabled]="isSubmitting || mediaForm.invalid || !selectedFile"
      >
        <span *ngIf="!isSubmitting">Upload & Analyze Serve</span>
        <span *ngIf="isSubmitting">Uploading…</span>
      </button>
    </form>

    <div class="divider"></div>

    <!-- Filters -->
    <form [formGroup]="searchForm" class="filters">
      <div class="field">
        <label>Search</label>
        <input
          type="text"
          formControlName="query"
          placeholder="Search by title, label, or transcript…"
        />
      </div>

      <div class="field">
        <label>Labels</label>
        <div class="label-chips">
          <button
            type="button"
            class="chip"
            [class.active]="!selectedLabel"
            (click)="selectLabel(null)"
          >
            All
          </button>
          <button
            type="button"
            class="chip"
            *ngFor="let lbl of labels"
            [class.active]="lbl === selectedLabel"
            (click)="selectLabel(lbl)"
          >
            {{ lbl }}
          </button>
        </div>
      </div>

      <div class="field field--inline">
        <label class="checkbox">
          <input
            type="checkbox"
            [checked]="showArchived"
            (change)="toggleArchivedView()"
          />
          <span>Show archived serves</span>
        </label>
      </div>
    </form>
  </div>

  <!-- Right column: serves grid -->
  <div class="content">
    <!-- Pinned section -->
    <section class="section">
      <div class="section__header">
        <h3>Pinned serves</h3>
        <span class="section__count">{{ filteredPinned.length }}</span>
      </div>

      <div
        cdkDropList
        [cdkDropListData]="pinnedItems"
        class="card-grid"
        (cdkDropListDropped)="dropPinned($event)"
      >
        <div
          class="serve-card"
          *ngFor="let item of filteredPinned"
          cdkDrag
          [style.backgroundColor]="item.color || '#ffffff'"
        >
          <div class="serve-card__header">
            <div class="serve-badge-wrapper" *ngIf="item.pose">
              <span
                class="serve-badge"
                [ngClass]="getServeBadgeClass(item)"
              >
                {{ getServeBadgeText(item) }}
              </span>
            </div>

            <div class="status-pill" [class.status-pill--processing]="item.status === 'processing'">
              <span *ngIf="item.status === 'uploading'">Uploading</span>
              <span *ngIf="item.status === 'processing'">Analyzing</span>
              <span *ngIf="item.status === 'ready'">Ready</span>
              <span *ngIf="!item.status">Unknown</span>
            </div>

            <div class="header-actions">
              <button
                type="button"
                class="icon-button"
                title="Unpin"
                (click)="togglePinned(item, false)"
              >
                <i class="fa-solid fa-thumbtack"></i>
              </button>
              <button
                type="button"
                class="icon-button"
                title="Archive / restore"
                (click)="archive(item)"
              >
                <i class="fa-solid" [ngClass]="item.archived ? 'fa-box-open' : 'fa-box-archive'"></i>
              </button>
            </div>
          </div>

          <div class="serve-card__thumbnail" (click)="openMedia(item)">
            <img
              *ngIf="item.thumbnailUrl; else noThumbPinned"
              [src]="item.thumbnailUrl"
              alt="Serve thumbnail"
            />
            <ng-template #noThumbPinned>
              <div class="thumbnail-placeholder">
                <i class="fa-solid fa-video"></i>
              </div>
            </ng-template>

            <div
              class="trophy-angle"
              *ngIf="item.pose?.trophy_angle_deg != null"
            >
              {{ item.pose.trophy_angle_deg | number:'1.0-1' }}°
              <span *ngIf="item.pose?.trophy_timestamp_s != null">
                @ {{ item.pose.trophy_timestamp_s | number:'1.1-1' }}s
              </span>
            </div>
          </div>

          <div class="serve-card__body">
            <h4 class="serve-title">
              {{ item.title || 'Untitled serve' }}
            </h4>
            <p class="serve-description" *ngIf="item.description">
              {{ item.description }}
            </p>

            <div class="serve-labels" *ngIf="item.labels?.length">
              <span
                class="label-chip"
                *ngFor="let lbl of item.labels"
                (click)="selectLabel(lbl)"
                [class.active]="lbl === selectedLabel"
              >
                {{ lbl }}
              </span>
            </div>
          </div>

          <div class="serve-card__footer">
            <div class="serve-meta">
              <span class="date">
                {{ item.createdAt | date:'short' }}
              </span>
              <span class="media-type" *ngIf="item.mediaType === 'video'">
                Video
              </span>
            </div>

            <div class="serve-actions">
              <button
                type="button"
                class="text-button"
                (click)="reanalyze(item)"
                [disabled]="item.status === 'processing'"
              >
                Re-analyze
              </button>
              <button
                type="button"
                class="text-button"
                (click)="useInChat(item)"
              >
                Use in chat
              </button>

              <div class="color-dots">
                <button
                  type="button"
                  *ngFor="let c of colorPalette"
                  class="color-dot"
                  [style.backgroundColor]="c"
                  [class.selected]="item.color === c"
                  (click)="changeColor(item, c)"
                ></button>
              </div>

              <button
                type="button"
                class="text-button text-button--danger"
                (click)="delete(item)"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Other serves -->
    <section class="section">
      <div class="section__header">
        <h3>All serves</h3>
        <span class="section__count">{{ filteredOthers.length }}</span>
      </div>

      <div
        cdkDropList
        [cdkDropListData]="otherItems"
        class="card-grid"
        (cdkDropListDropped)="dropOthers($event)"
      >
        <div
          class="serve-card"
          *ngFor="let item of filteredOthers"
          cdkDrag
          [style.backgroundColor]="item.color || '#ffffff'"
        >
          <div class="serve-card__header">
            <div class="serve-badge-wrapper" *ngIf="item.pose">
              <span
                class="serve-badge"
                [ngClass]="getServeBadgeClass(item)"
              >
                {{ getServeBadgeText(item) }}
              </span>
            </div>

            <div class="status-pill" [class.status-pill--processing]="item.status === 'processing'">
              <span *ngIf="item.status === 'uploading'">Uploading</span>
              <span *ngIf="item.status === 'processing'">Analyzing</span>
              <span *ngIf="item.status === 'ready'">Ready</span>
              <span *ngIf="!item.status">Unknown</span>
            </div>

            <div class="header-actions">
              <button
                type="button"
                class="icon-button"
                title="Pin serve"
                (click)="togglePinned(item, true)"
              >
                <i class="fa-regular fa-thumbtack"></i>
              </button>
              <button
                type="button"
                class="icon-button"
                title="Archive / restore"
                (click)="archive(item)"
              >
                <i class="fa-solid" [ngClass]="item.archived ? 'fa-box-open' : 'fa-box-archive'"></i>
              </button>
            </div>
          </div>

          <div class="serve-card__thumbnail" (click)="openMedia(item)">
            <img
              *ngIf="item.thumbnailUrl; else noThumbOther"
              [src]="item.thumbnailUrl"
              alt="Serve thumbnail"
            />
            <ng-template #noThumbOther>
              <div class="thumbnail-placeholder">
                <i class="fa-solid fa-video"></i>
              </div>
            </ng-template>

            <div
              class="trophy-angle"
              *ngIf="item.pose?.trophy_angle_deg != null"
            >
              {{ item.pose.trophy_angle_deg | number:'1.0-1' }}°
              <span *ngIf="item.pose?.trophy_timestamp_s != null">
                @ {{ item.pose.trophy_timestamp_s | number:'1.1-1' }}s
              </span>
            </div>
          </div>

          <div class="serve-card__body">
            <h4 class="serve-title">
              {{ item.title || 'Untitled serve' }}
            </h4>
            <p class="serve-description" *ngIf="item.description">
              {{ item.description }}
            </p>

            <div class="serve-labels" *ngIf="item.labels?.length">
              <span
                class="label-chip"
                *ngFor="let lbl of item.labels"
                (click)="selectLabel(lbl)"
                [class.active]="lbl === selectedLabel"
              >
                {{ lbl }}
              </span>
            </div>
          </div>

          <div class="serve-card__footer">
            <div class="serve-meta">
              <span class="date">
                {{ item.createdAt | date:'short' }}
              </span>
              <span class="media-type" *ngIf="item.mediaType === 'video'">
                Video
              </span>
            </div>

            <div class="serve-actions">
              <button
                type="button"
                class="text-button"
                (click)="reanalyze(item)"
                [disabled]="item.status === 'processing'"
              >
                Re-analyze
              </button>
              <button
                type="button"
                class="text-button"
                (click)="useInChat(item)"
              >
                Use in chat
              </button>

              <div class="color-dots">
                <button
                  type="button"
                  *ngFor="let c of colorPalette"
                  class="color-dot"
                  [style.backgroundColor]="c"
                  [class.selected]="item.color === c"
                  (click)="changeColor(item, c)"
                ></button>
              </div>

              <button
                type="button"
                class="text-button text-button--danger"
                (click)="delete(item)"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</div>
